import type { SidedLayout } from "@pdfking/filling-flex"
import { LayoutLeaves, LayoutPages } from "@pdfking/filling-flex"
import { NullQ } from "@pdfking/structs"
import type { GUID } from "@pdfking/tfunc"

import type { ImpositionSchema } from "#internal"
import type { Hook, HookCtx } from "#internal"
import type { ImpositionLeaf, ImpositionNodeData } from "#internal"
import type { Numbering } from "#internal"
import type { PackedSheet, SheetSlot } from "#internal"

import { BookQ } from "../../common/BookQ"
import { PathKey } from "../../common/layout/PathKey"
import { ApplyFinalHooks, ApplyHooks } from "../hooks/ApplyHooks"
import { NumberPages } from "../numbering/NumberPages"
import { PageEntryOf } from "../numbering/PageEntryOf"
import { ResolveSortFunction } from "../numbering/ResolveSortFunction"
import { BuildSheets } from "../presets/BuildSheets"

/** A sheet's layout laid out: the box it stands in is its own. */
export const ResolveSheet = (sheet: PackedSheet): SidedLayout<ImpositionNodeData> =>
  LayoutPages(sheet.layout, { filling: sheet.filling })

// Every place on the sheet, with the pages that land there: they share the
// path above the side.
const slots_of = (pages: ImpositionLeaf[]): SheetSlot[] => {
  const slots = new Map<string, SheetSlot>()
  for (const page of pages) {
    const key = PathKey(page.guid.slice(0, -1))
    const slot = slots.get(key) ?? { pages: [], page_total: page.data?.page?.page_total ?? 1 }
    slots.set(key, { ...slot, pages: [...slot.pages, page.guid] })
  }
  return [...slots.values()]
}

const sheet_hook_ctx = (
  input: ImpositionSchema,
  sheet: PackedSheet,
  numbering: Numbering,
  uuid: GUID
): HookCtx => {
  const { info } = sheet
  return {
    job_id: uuid,
    filename: input.filename,
    page_informations: numbering.informations,
    division: NullQ(info.repetition_number) ? undefined : info.division,
    repetition_number: info.repetition_number,
    side: info.side,
    horizon_components: input.component_array,
  }
}

/**
 * The sheets of a job, hooked and in final order, and the numbering of their
 * pages.
 *
 * The pages are numbered off the sheets as the preset built them: a hook only
 * hangs drawings on a sheet or turns it, and a page's identity is its path,
 * which neither changes. Neither needs the sheets laid out: the pages and the
 * slots are read off the layouts, which are laid out only when drawn.
 */
export const PipelineSheets = (
  input: ImpositionSchema,
  uuid: GUID,
  // `marking` is already requires-checked + sequence-sorted by the form's
  // ValidateJobProcessors cast.
  processors: Hook[] = input.marking
) => {
  const is_book = BookQ(input)

  const preset_ctx: HookCtx = {
    horizon_components: input.component_array,
    orientation_opening_top: input.orientation_kind.opening_top,
    is_book,
    // The job uuid (= the dispatch's imposition_id) — the universe barcode hook
    // captures it at setup for its job-number segment.
    job_id: uuid,
    filename: input.filename,
  }
  const { sheets: built, fills } = BuildSheets(input, processors, preset_ctx)
  const leaves = built.map(sheet => [...LayoutLeaves(sheet.layout)])

  const numbering = NumberPages(
    leaves.flatMap(pages => pages.flatMap(page => PageEntryOf(page) ?? [])),
    ResolveSortFunction(input)
  )

  const sheets: PackedSheet[] = built.map((sheet, i) => ({
    ...sheet,
    info: { ...sheet.info, slots: slots_of(leaves[i]!) },
  }))

  const hooked = sheets.map(sheet => ({
    ...sheet,
    layout: ApplyHooks(processors, sheet_hook_ctx(input, sheet, numbering, uuid), sheet.layout),
  }))

  // Python `pipeline_plot_pages` passes the (job, pages) pair through
  // `final_job_processor` (plotter.py:142). The hooks chain — currently
  // just `universe_job_processor` — may reorder the entries.
  const final = ApplyFinalHooks(
    processors,
    { job_id: uuid, filename: input.filename, page_informations: numbering.informations, is_book },
    hooked
  )

  return { numbering, processors, sheets: final, fills, is_book }
}
